"use client";

import { ButtonHTMLAttributes, ReactNode } from "react";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "outline";
type ButtonSize = "sm" | "md" | "lg";

interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  children?: ReactNode;
}

const baseClasses =
  "inline-flex items-center justify-center font-medium transition-colors duration-100 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-secondary disabled:opacity-50 disabled:cursor-not-allowed";

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-accent-primary hover:bg-accent-hover active:bg-accent-active text-inverse focus:ring-accent-primary/40",
  secondary:
    "bg-tertiary hover:bg-hover text-primary border border-primary focus:ring-primary/30",
  danger:
    "bg-status-error hover:opacity-90 text-inverse focus:ring-status-error/40",
  ghost:
    "bg-transparent text-primary hover:bg-hover focus:ring-primary/30",
  outline:
    "border border-primary bg-secondary text-primary hover:bg-hover focus:ring-primary/30",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-meta-sm rounded-meta-lg",
  md: "h-9 px-4 text-meta-base rounded-meta-lg",
  lg: "h-10 px-5 text-meta-base rounded-meta-lg",
};

export function BaseButton({
  variant = "primary",
  size = "md",
  type = "button",
  disabled = false,
  loading = false,
  fullWidth = false,
  iconBefore,
  iconAfter,
  children,
  className = "",
  ...props
}: BaseButtonProps) {
  const buttonClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={buttonClasses}
      {...props}
    >
      <span className="flex items-center">
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        )}

        {iconBefore && <span className="mr-2">{iconBefore}</span>}

        {children}

        {iconAfter && <span className="ml-2">{iconAfter}</span>}
      </span>
    </button>
  );
}
